import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { Button } from 'components/universal/button';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { addNewUser } from 'services/registration';
import { APIErrors } from 'types/api';
import { Input } from '../input';
import cls from './addNewUser.module.scss';
import { schema } from './validation';

export interface AddNewUserValue {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    employee_position_name: string;
}

const defaultValues = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    employee_position_name: '',
};

export type AddNewUserRequest = {
    data: AddNewUserValue;
  };

interface AddNewUserFormInterface {
    workplaceId: number;
    onCancel: (boolean) => void;
}

export const AddNewUserForm = ({ 
    workplaceId, 
    onCancel 
}: AddNewUserFormInterface) => {
    const { t } = useTranslation();
    
    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<AddNewUserValue>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues,
    });

    const onSubmit = (formObject: AddNewUserValue) => {
        const prepareData = formObject; 
        prepareData['workplace_id'] = workplaceId;
        addNewUserData.mutate({
            data: prepareData,
        });
    };

    const addNewUserData = useMutation<
        AxiosResponse<{ message: string }>,
        AxiosError<APIErrors>,
        AddNewUserRequest
    >((payload) => addNewUser(payload), {
        onSuccess: (data) => {
            if (data.status === 201) {
                toast.success(t('employeeManagement.created'));
                onCancel(true);
            }
        },
        onError: (error) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof AddNewUserValue, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    return (
        <>
            <h3 className={cls.modalHeader}>
                {t('employeeManagement.addNewUser')}
            </h3>
            <form
                className={cls.form}
                onSubmit={handleSubmit(onSubmit)}
                noValidate
            >
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="first_name"
                    name="first_name"
                    label={t('registration.firstNameRequire')}
                    error={t(errors.first_name?.message)}
                    maxLength={255}
                />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="last_name"
                    name="last_name"
                    label={t('registration.lastNameRequire')}
                    error={t(errors.last_name?.message)}
                    maxLength={255}
                />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="phone"
                    name="phone"
                    label={t('registration.phone')}
                    error={t(errors.phone?.message)}
                    maxLength={255}
                />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="employee_position_name"
                    name="employee_position_name"
                    label={t('registration.position')}
                    error={t(errors.employee_position_name?.message)}
                    maxLength={255}
                />
                <div className={cls.modalButtonWrapper}>
                    <Button
                        className={cls.modalButton}
                        styleType="border"
                        type="button"
                        onClick={onCancel}
                    >
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.send')}
                    </Button>
                </div>
            </form>
        </>
    );
};
